import React, { useRef, useState } from 'react';
import { Alert, Button, Dropdown, Modal, Spinner } from 'react-bootstrap';
import applicationService from '@/shared/services/applicationService';
import ActionBar from '@/components/common/ActionBar';

/**
 * Withdraws a submitted application after a confirmation. The record is kept
 * and the ranked programs' coordinators are told. A draft is deleted instead
 * (DeleteDraftButton). `asMenuItem` renders the trigger as an item of a
 * "More" dropdown menu, which keeps it mounted once opened, so the dialog
 * outlives the menu closing.
 */
// What to do instead of withdrawing depends on the request's status: only a
// submitted request offers Revise (the page's canRevise, and the server's
// "Only a submitted request can be revised"), and a returned one is already
// open for changes.
const CHANGE_INSTEAD = {
    submitted: (
        <>
            Need to change something instead? You don&apos;t need to withdraw:
            use <strong>Revise this request</strong> and resubmit it.
        </>
    ),
    revision_requested: (
        <>
            Need to change something instead? You don&apos;t need to withdraw:
            make your changes and resubmit the request.
        </>
    ),
};
const CHANGE_INSTEAD_OTHERWISE =
    "Need to change something instead? Ask the program's coordinator.";

function WithdrawApplicationButton({
    applicationId,
    courseLabel,
    status,
    onWithdrawn,
    onRefused,
    asMenuItem = false,
}) {
    const [show, setShow] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);
    const [error, setError] = useState(null);
    const [stale, setStale] = useState(false);
    const triggerRef = useRef(null);
    const titleId = `withdraw-application-title-${applicationId}`;

    const close = () => {
        if (withdrawing) return;
        setShow(false);
        setError(null);
        if (stale) {
            setStale(false);
            onRefused?.();
        }
    };

    const confirm = async () => {
        setWithdrawing(true);
        setError(null);
        try {
            await applicationService.withdrawApplication(applicationId);
            setShow(false);
            onWithdrawn?.(applicationId);
        } catch (err) {
            // A refusal usually means the application changed (e.g. a program
            // decided it), so the page reloads it once the message is closed
            setStale(true);
            setError(
                err.response?.data?.message ||
                    'The application could not be withdrawn. Please try again.'
            );
        } finally {
            setWithdrawing(false);
        }
    };

    return (
        <>
            {asMenuItem ? (
                <Dropdown.Item
                    as="button"
                    ref={triggerRef}
                    className="text-danger"
                    onClick={() => setShow(true)}
                    aria-label={`Withdraw application for ${courseLabel}`}
                >
                    Withdraw application
                </Dropdown.Item>
            ) : (
                <Button
                    variant="outline-danger"
                    onClick={() => setShow(true)}
                    aria-label={`Withdraw application for ${courseLabel}`}
                >
                    Withdraw application
                </Button>
            )}
            <Modal
                show={show}
                onHide={close}
                aria-labelledby={titleId}
                // A menu item is hidden once its menu closes, so focus goes
                // back to the menu's toggle instead
                restoreFocus={!asMenuItem}
                onExited={() => {
                    if (!asMenuItem) return;
                    triggerRef.current
                        ?.closest('.dropdown')
                        ?.querySelector('.dropdown-toggle')
                        ?.focus();
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title id={titleId} as="h2" className="h5">
                        Withdraw this request?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Your request for {courseLabel} will no longer be
                        reviewed. It moves to the Past tab, where you can reopen
                        it and resubmit it while the request window is open.
                    </p>
                    <p className="mb-0">
                        {CHANGE_INSTEAD[status] || CHANGE_INSTEAD_OTHERWISE}
                    </p>
                    {error && (
                        <Alert variant="danger" className="mt-3 mb-0">
                            {error}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <ActionBar
                        className="w-100"
                        secondary={
                            <Button
                                variant="outline-primary"
                                onClick={close}
                                disabled={withdrawing}
                            >
                                Keep application
                            </Button>
                        }
                        primary={
                            <Button
                                variant="danger"
                                onClick={confirm}
                                disabled={withdrawing}
                            >
                                {withdrawing ? (
                                    <>
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            className="me-2"
                                            aria-hidden="true"
                                        />
                                        Withdrawing…
                                    </>
                                ) : (
                                    'Withdraw'
                                )}
                            </Button>
                        }
                    />
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default WithdrawApplicationButton;
